import { z } from "zod";
import {
  PeppolProvider,
  PeppolEnvironment,
  PeppolOnboardingStatus,
  PeppolParticipantIdentifierSchema,
  PeppolProviderCapabilitiesSchema,
  PeppolTenantStatusSchema,
} from "../schemas/PeppolSchema";
const tenant = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);
const revision = z.string().regex(/^[a-f0-9]{64}$/);
export const PeppolConfigurationAssignmentSchema = z
  .object({
    provider: PeppolProvider,
    environment: PeppolEnvironment,
    countryCode: z.string().regex(/^[A-Z]{2}$/),
    status: PeppolOnboardingStatus,
    version: z.number().int().positive().safe(),
    participantId: z
      .string()
      .min(3)
      .max(255)
      .regex(/^[^\u0000-\u001f\u007f]+$/)
      .optional(),
    lastSynchronizedAt: z.string().datetime().optional(),
  })
  .strict();
export const PeppolConfigurationSchema = z
  .object({
    tenantId: tenant,
    revision,
    assignment: PeppolConfigurationAssignmentSchema.nullable(),
    capabilities: PeppolProviderCapabilitiesSchema.extend({
      supportedCountryCodes: z.array(z.string().regex(/^[A-Z]{2}$/)).max(300),
      productionIneligibilityReasons:
        PeppolProviderCapabilitiesSchema.shape.productionIneligibilityReasons.max(
          10,
        ),
    }).nullable(),
    sendEligible: z.boolean(),
    receiveEligible: z.boolean(),
    countryEligible: z.boolean(),
    environmentEligible: z.boolean(),
    onboardingAvailable: z.boolean(),
    nextAction: PeppolTenantStatusSchema.shape.nextAction,
  })
  .strict()
  .superRefine((value, ctx) => {
    const active = value.assignment?.status === "ACTIVE",
      qualified = value.countryEligible && value.environmentEligible;
    if (
      (value.sendEligible &&
        !(active && qualified && value.capabilities?.exactUblSend)) ||
      (value.receiveEligible &&
        !(active && qualified && value.capabilities?.inboundUbl)) ||
      (value.onboardingAvailable &&
        !(
          value.assignment?.status === "UNASSIGNED" &&
          !value.assignment.participantId &&
          qualified &&
          value.capabilities?.provisioning
        ))
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assignment"],
        message: "Inconsistent lifecycle eligibility",
      });
    if (
      Boolean(value.assignment) !== Boolean(value.capabilities) ||
      (value.assignment &&
        value.capabilities &&
        (value.assignment.provider !== value.capabilities.provider ||
          value.assignment.environment !== value.capabilities.environment))
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assignment"],
        message: "Inconsistent assignment capability",
      });
    if (
      !value.assignment &&
      (value.sendEligible ||
        value.receiveEligible ||
        value.countryEligible ||
        value.environmentEligible ||
        value.onboardingAvailable)
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assignment"],
        message: "Unassigned connection is unavailable",
      });
  });
export const StartPeppolOnboardingCommandSchema = z
  .object({
    expectedRevision: revision,
    expectedAssignment: z
      .object({
        version: z.number().int().positive().safe(),
        provider: PeppolProvider,
        environment: PeppolEnvironment,
      })
      .strict(),
    participant: PeppolParticipantIdentifierSchema.extend({
      value: PeppolParticipantIdentifierSchema.shape.value.regex(
        /^[^\u0000-\u001f\u007f]+$/,
      ),
    }),
  })
  .strict();
export const PeppolConfigurationIssueSchema = z
  .object({
    path: z.enum(["participant.scheme", "participant.value", "form"]),
    code: z.enum(["REQUIRED", "INVALID", "TOO_LONG"]),
  })
  .strict();
export type PeppolConfiguration = z.infer<typeof PeppolConfigurationSchema>;
export type StartPeppolOnboardingCommand = z.infer<
  typeof StartPeppolOnboardingCommandSchema
>;
export type PeppolConfigurationIssue = z.infer<
  typeof PeppolConfigurationIssueSchema
>;
